import {
	Form,
	FormItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import {
	AddTilesResponseModel,
	AddressBookDetailsTileModel,
	AddressBookViewKind,
	AddressBookUpdateTileModel,
	updateAddressBookTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useMemo,
	useState,
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { ExistingTile } from "./common";
import { EditTileFromData, initialGenericState } from "./EditGenericTile";
import { EditTileHandle } from "./EditTile";
import { EditTileGeneralFields } from "./GeneralFields";
import { useUpdateTile } from "./hooks/useUpdateTile";

export type EditAddressBookTileProps = {
	isLoading: boolean;
	onIsLoadingChange(isLoading: boolean): void;
	initialModel: AddressBookDetailsTileModel;
	mode: ExistingTile;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditAddressBookTile = forwardRef(
	(props: EditAddressBookTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			isLoading,
			onIsLoadingChange,
			initialModel,
			mode,
			onAfterSave
		} = props;

		const [formData, setFormData] = useState<AddressBookEditFormData>({
			...initialGenericState(initialModel),
			defaultView: initialModel.defaultView
		});

		const formHandler: IFormHandler<AddressBookEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const { columnSpan, rowSpan } = formHandler.for("dimension")
			.value;

		const isDefaultViewNeeded: boolean = useMemo(
			(): boolean => computeIsDefaultViewNeeded(columnSpan, rowSpan),
			[columnSpan, rowSpan]
		);

		const { updateHandler } = useUpdateTile<AddressBookUpdateTileModel>(
			formHandler,
			updateAddressBookTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			() => ({
				updateHandler
			}),
			[updateHandler]
		);

		return (
			<Form onSubmit="NONE">
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				{isDefaultViewNeeded && (
					<FormItem label={localize("_DefaultView")}>
						<Select
							{...formHandler
								.for("defaultView")
								.attachToSelect()}
							options={defaultViewOptions}
							menuTitle={localize("_DefaultView")}
						/>
						<ValidationError
							{...formHandler.for("defaultView").attachToValidationError()}
						/>
					</FormItem>
				)}
			</Form>
		);
	}
);

EditAddressBookTile.displayName = "EditAddressBookTile";

function computeIsDefaultViewNeeded(
	columnSpan: number,
	rowSpan: number
): boolean {
	return columnSpan >= 4 && rowSpan >= 4;
};

function prepareModelForSaving(formData: AddressBookEditFormData): AddressBookEditFormData {
	const { columnSpan, rowSpan } = formData.dimension;
	const isDefaultViewNeeded: boolean = computeIsDefaultViewNeeded(columnSpan, rowSpan);

	return {
		...formData,
		defaultView: isDefaultViewNeeded
			? formData.defaultView
			: null
	};
};

const defaultViewOptions: readonly SelectItem<AddressBookViewKind>[] = [
	{
		text: localize("_WorkspaceContacts"),
		value: AddressBookViewKind.Workspace
	},
	{
		text: localize("_OwnDepartment"),
		value: AddressBookViewKind.Department
	},
	{
		text: localize("_PersonalContacts"),
		value: AddressBookViewKind.Personal
	},
	{
		text: localize("_OrganisationContacts"),
		value: AddressBookViewKind.Organization
	}
];

const validationBuilder: IValidationConfiguration<AddressBookEditFormData> = createValidationConfiguration<AddressBookEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationBuilder.when(
	(formData): boolean => {
		const { columnSpan, rowSpan } = formData.dimension;
		return computeIsDefaultViewNeeded(columnSpan, rowSpan);
	}
)
	.forScalar("defaultView")
	.add(
		isNotNullNorUndefined()
	);

type AddressBookEditFormData = EditTileFromData<AddressBookUpdateTileModel>;